import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { applyAxiosXsrf } from './xsrf';

export type InactivityTimerOptions = {
	/**
	 * the date on which the authentication ticket will be invalid
	 */
	ticketExpiration: Dayjs;

	/**
	 * a URL which can be POSTed to keep the session alive
	 */
	keepAliveUrl: string;

	/**
	 * a formatted URL which the user will be taken to after logout
	 */
	postLogoutUrlFormat: string;

	/**
	 * a URL which can be submitted to which will trigger a logout when done so
	 */
	logoutUrl: string;

	/**
	 * the current date as reported by the server, in order to determine skew with the client
	 */
	serverNow: Dayjs;

	/**
	 * the current date as reported by the browser, in order to determine skew with the server
	 */
	clientNow: Dayjs;

	/**
	 * how much time in seconds before session expiration to pop open the modal
	 */
	timeBeforeSessionExpirationToShowModal: number;

	/**
	 * how much time in seconds to offset the actual expiration time. setting
	 * this to a non-zero number may be needed in order to preserve anti-xss
	 * on the logout action.
	 */
	sessionExpirationOffset: number;
};

export class InactivityTimer {
	private options: InactivityTimerOptions;
	private inactivityTimer?: number;
	private countdownTimer?: number;

	// represents the clock skew of the client compared to the server.
	// a positive number means that the server is ahead of the client, a negative
	// number means the server is behind the client.
	private clientClockSkew = 0;

	constructor (options: InactivityTimerOptions) {
		this.options = options;

		this.ticketExpired = false;
		this.ticketExpiration = this.options.ticketExpiration;
	}

	public startTimers (): void {
		// the inactivity timer is always running, and controls
		// whether the modal should be shown (ttl <= 1 minute),
		// and whether the user should be redirected to the login page (ttl <= 0 minutes)
		this.startInactivityTimer();

		// the countdown timer updates the on-screen countdown in the modal
		this.startCountdownTimer();
	}

	public updateClockSkew (clientDate: Dayjs, serverDate: Dayjs) {
		const skew = serverDate.diff(clientDate, 'ms');
		this.clientClockSkew = skew;
	}

	private startInactivityTimer (): void {
		this.stopInactivityTimer();
		this.inactivityTimer = setInterval(() => this.onInactivityTimerTick(), 1000);
	}

	private stopInactivityTimer (): void {
		if (this.inactivityTimer) {
			clearInterval(this.inactivityTimer);
		}
	}

	private async onInactivityTimerTick () {
		const ticketExpirationInSeconds = this.ticketExpirationInSeconds;

		if (localStorage.getItem('ticketExpired') != null) {
			this.stopInactivityTimer();
			await this.forceLogout(false);
		} else if (ticketExpirationInSeconds <= 0) {
			this.stopInactivityTimer();
			await this.forceLogout(true);
		} else if (ticketExpirationInSeconds <= this.options.timeBeforeSessionExpirationToShowModal) {
			this.showModal();
		} else {
			this.hideModal();
		}
	}

	private startCountdownTimer (): void {
		this.stopCountdownTimer();
		this.countdownTimer = setInterval(() => this.onCountdownTimerTick(), 1000);
	}

	private stopCountdownTimer (): void {
		if (this.countdownTimer) {
			clearInterval(this.countdownTimer);
		}
	}

	private onCountdownTimerTick (): void {
		const expirationInSeconds = this.ticketExpirationInSeconds;

		if (expirationInSeconds <= 0) {
			this.inactivityGuardWarningTimerDisplay = '0';
		} else {
			this.inactivityGuardWarningTimerDisplay = expirationInSeconds.toString();
		}
	}

	private async onActivityGuardContinueClick () {
		await axios.post(this.options.keepAliveUrl);
		// HACK: keepAliveUrl needs to be called twice, because the server can't
		// emit the expiry on the same request which regenerates the session
		const resp = await axios.post(this.options.keepAliveUrl);

		this.updateClockSkew(dayjs(), dayjs(resp.headers.date));

		const expiry = resp.headers['x-session-expiry'];
		this.ticketExpiration = dayjs(expiry);

		this.hideModal();
	}

	private async onActivityGuardCancelClick () {
		await this.forceLogout(false, true);
	}

	private showModal (): void {
		const currentModal = document.getElementById('inactivity-countdown');

		if (currentModal == null) {
			this.createModal();
		}
	}

	private hideModal (): void {
		const currentModal = document.getElementById('inactivity-countdown');

		if (currentModal != null) {
			this.destroyJqueryModalInstance(currentModal);
		}
	}

	private createModal (): void {
		this.createJqueryModalInstance();

		const cancelButton = document.getElementById('inactivity-countdown__cancel');
		const continueButton = document.getElementById('inactivity-countdown__continue');

		if (cancelButton != null) {
			cancelButton.addEventListener('click', () => this.onActivityGuardCancelClick());
		}

		if (continueButton != null) {
			continueButton.addEventListener('click', () => this.onActivityGuardContinueClick());
		}
	}

	private createJqueryModalInstance () {
		let modal: HTMLElement | DocumentFragment = document.createRange().createContextualFragment(this.modalTemplate);
		document.body.appendChild(modal);

		// HACK: calling bootstrap modal with a document fragment
		// causes an error in bootstrap.
		modal = document.getElementById('inactivity-countdown')!;

		$(modal).modal({
			backdrop: 'static'
		});
	}

	private destroyJqueryModalInstance (modal: HTMLElement) {
		$(modal).modal('hide');
		modal.remove();
	}

	private async forceLogout (dueToInactivity = false, fromUserInteraction = false) {
		let redirectLocation = this.options.postLogoutUrlFormat.replace('{0}', dueToInactivity.toString().toLowerCase());

		try {
			const data = { fromAutomaticLogout: dueToInactivity };
			const config = {
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
			};
			applyAxiosXsrf(config);

			const resp = await axios.post(this.options.logoutUrl, data, config);

			this.ticketExpired = true;
			if (fromUserInteraction && (resp.status === 200 || resp.status === 302)) {
				redirectLocation = resp.status === 200 ? resp.request.responseURL : resp.headers.location;
			}
		} finally {
			window.location.replace(redirectLocation);
		}
	}

	get now () {
		return dayjs().add(this.clientClockSkew, 'ms');
	}

	get inactivityGuardWarningTimerDisplay (): string | null {
		const inactivityGuardWarningTimerDomElement = document.getElementById('inactivity-countdown__timer');

		if (inactivityGuardWarningTimerDomElement != null) {
			return inactivityGuardWarningTimerDomElement.innerText;
		}

		return null;
	}

	set inactivityGuardWarningTimerDisplay (value: string | null) {
		const inactivityGuardWarningTimerDomElement = document.getElementById('inactivity-countdown__timer');

		if (inactivityGuardWarningTimerDomElement != null) {
			inactivityGuardWarningTimerDomElement.innerText = value || '';
		}
	}

	get modalTemplate (): string {
		return `<div class="modal fade" id="inactivity-countdown" role="dialog" aria-hidden="true" style="z-index: 99999">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<span class="modal-title lead">Continue session?</span>
					</div>
					<div class="modal-body">
						<p>
							For your security, you will be automatically signed out in <span id="inactivity-countdown__timer">${this.options.timeBeforeSessionExpirationToShowModal}</span> seconds.
						</p>
						<p>
							Do you want to continue your session?
						</p>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-default" id="inactivity-countdown__cancel">No</button>
						<button type="button" class="btn btn-primary" id="inactivity-countdown__continue">Yes</button>
					</div>
				</div>
			</div>
		</div>`;
	}

	get ticketExpirationInSeconds () {
		// the offset is a buffer to ensure that the POST to forceLogout is successful
		// without removing anti-xsrf from that URL
		const offset = this.options.sessionExpirationOffset;
		const expiration = this.ticketExpiration;
		const now = this.now;
		const expirationInSeconds = expiration.diff(now, 's');

		return expirationInSeconds - offset;
	}

	get ticketExpired (): boolean {
		// this uses local storage to synchronize between multiple tabs/windows
		return localStorage.getItem('ticketExpired') === 'true';
	}

	set ticketExpired (value: boolean) {
		if (value) {
			localStorage.setItem('ticketExpired', 'true');
		} else {
			localStorage.removeItem('ticketExpired');
		}
	}

	get ticketExpiration (): Dayjs {
		// this uses local storage to synchronize between multiple tabs/windows
		return dayjs(localStorage.getItem('ticketExpiration'));
	}

	set ticketExpiration (value: Dayjs) {
		localStorage.setItem('ticketExpiration', value.toISOString());
	}
}

export function initializeInactivityTimer (options: InactivityTimerOptions) {
	const isSessionAuthenticated = options.ticketExpiration.isValid();

	if (isSessionAuthenticated) {
		const appendTarget = window as any;

		const timer = new InactivityTimer(options);

		appendTarget.__InactivityTimer__ = timer;
		timer.startTimers();

		if (options.clientNow.isValid() && options.serverNow.isValid()) {
			timer.updateClockSkew(options.clientNow, options.serverNow);
		}
	}
}

export function initializeInactivityTimerWithDefaultOptions (clientNow: Dayjs) {
	const ticketExpirationInput = document.getElementsByName('ticketExpiration')[0] as HTMLInputElement;
	const serverClockInput = document.getElementById('serverClock') as HTMLInputElement;
	const serverNow = dayjs(serverClockInput != null ? serverClockInput.value : null);
	const ticketExpiration = dayjs(ticketExpirationInput != null ? ticketExpirationInput.value : null);

	initializeInactivityTimer({
		keepAliveUrl: '/Account/KeepAlive',
		logoutUrl: '/Identity/Account/Logout',
		postLogoutUrlFormat: '/Identity/Account/Login?fromAutomaticLogout={0}',
		ticketExpiration,
		clientNow,
		serverNow,
		timeBeforeSessionExpirationToShowModal: 120,
		sessionExpirationOffset: 60
	});
}
